import { ArrowRight, ShieldCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { PK_COLORS } from '../PathkitLogo';

export function MarketingHero() {
  return (
    <section className="relative overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            `radial-gradient(900px 500px at 80% -10%, ${PK_COLORS.teal}22 0%, transparent 60%),` +
            `radial-gradient(700px 400px at 0% 0%, ${PK_COLORS.navy}11 0%, transparent 50%)`,
        }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <div
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600"
              role="note"
            >
              <ShieldCheck className="w-3.5 h-3.5" style={{ color: PK_COLORS.teal }} />
              Built for service desk consultants
            </div>
            <h1
              className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]"
              style={{ color: PK_COLORS.navy }}
            >
              Service desk implementations,{' '}
              <span style={{ color: PK_COLORS.teal }}>faster.</span>
            </h1>
            <p className="mt-5 text-lg text-slate-600 max-w-xl">
              PathKit gives consulting firms a single workspace to scope, design, and deliver
              service desk projects across Atlassian, ServiceNow, Ivanti, and custom platforms —
              with AI-assisted SOW analysis, guided discovery, and per-client isolation built in.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                asChild
                style={{ backgroundColor: PK_COLORS.navy, color: '#ffffff' }}
                className="hover:opacity-90"
              >
                <a
                  href="/book-demo"
                  className="inline-flex items-center gap-2"
                  style={{ color: '#ffffff' }}
                >
                  Book a demo <ArrowRight className="w-4 h-4" />
                </a>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <a href="#pricing">See pricing</a>
              </Button>
            </div>

            <p className="mt-5 text-xs text-slate-500">
              No credit card required to explore. Cancel any monthly plan anytime.
            </p>
          </div>

          <div className="relative">
            <HeroVisual />
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroVisual() {
  return (
    <div
      className="relative rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden"
      role="img"
      aria-label="Stylized PathKit workspace preview"
    >
      <div
        className="h-9 flex items-center gap-1.5 px-4 border-b border-slate-200"
        style={{ backgroundColor: '#F8FAFC' }}
      >
        <span className="w-2.5 h-2.5 rounded-full bg-slate-300" />
        <span className="w-2.5 h-2.5 rounded-full bg-slate-300" />
        <span className="w-2.5 h-2.5 rounded-full bg-slate-300" />
        <span className="ml-3 text-[11px] text-slate-500">acme.pathkit.pro</span>
      </div>

      <div className="grid grid-cols-12 gap-3 p-5">
        <div className="col-span-3 space-y-2">
          {['SOW Intake', 'Discovery', 'Spaces', 'Config Items', 'Suggestions', 'Client View'].map(
            (label, i) => (
              <div
                key={label}
                className="rounded-md px-2.5 py-2 text-[11px] font-medium"
                style={{
                  backgroundColor: i === 1 ? `${PK_COLORS.teal}1A` : 'transparent',
                  color: i === 1 ? PK_COLORS.navy : '#64748B',
                  border: i === 1 ? `1px solid ${PK_COLORS.teal}55` : '1px solid transparent',
                }}
              >
                {label}
              </div>
            ),
          )}
        </div>

        <div className="col-span-9 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div
                className="text-sm font-semibold"
                style={{ color: PK_COLORS.navy }}
              >
                Discovery Grid
              </div>
              <div className="text-[11px] text-slate-500">
                Acme Corp · Jira Service Management
              </div>
            </div>
            <span
              className="text-[10px] px-2 py-0.5 rounded-full"
              style={{ backgroundColor: `${PK_COLORS.navy}11`, color: PK_COLORS.navy }}
            >
              68% complete
            </span>
          </div>

          <div className="grid grid-cols-3 gap-2">
            {[
              { t: 'Request types', v: '24', s: PK_COLORS.teal },
              { t: 'Workflows', v: '11', s: PK_COLORS.navy },
              { t: 'Open questions', v: '7', s: '#94A3B8' },
            ].map((c) => (
              <div
                key={c.t}
                className="rounded-md border border-slate-200 px-3 py-2"
              >
                <div className="text-[10px] uppercase tracking-wide text-slate-500">{c.t}</div>
                <div className="text-lg font-semibold" style={{ color: c.s }}>
                  {c.v}
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-md border border-slate-200 divide-y divide-slate-100">
            {[
              { q: 'Which teams will use the portal?', a: 'IT, HR, Facilities' },
              { q: 'Will external customers raise tickets?', a: 'Yes — branded portal' },
              { q: 'What approvals are required?', a: 'Manager + finance over $1k' },
              { q: 'Existing automations to preserve?', a: '4 rules — see notes' },
            ].map((row) => (
              <div
                key={row.q}
                className="flex items-start justify-between gap-3 px-3 py-2 text-[11px]"
              >
                <span className="text-slate-700 flex-1">{row.q}</span>
                <span className="text-slate-500 text-right">{row.a}</span>
              </div>
            ))}
          </div>

          <div
            className="rounded-md p-3 text-[11px] flex items-start gap-2"
            style={{ backgroundColor: `${PK_COLORS.teal}10`, border: `1px solid ${PK_COLORS.teal}33` }}
          >
            <span
              className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-semibold text-white"
              style={{ backgroundColor: PK_COLORS.teal }}
            >
              AI
            </span>
            <span className="text-slate-700">
              Suggested next question:{' '}
              <span className="font-medium" style={{ color: PK_COLORS.navy }}>
                "What SLA targets apply to incidents originating from the customer portal?"
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
